'use client';

import { FC, ForwardedRef, forwardRef, useMemo, useRef, useState } from 'react';
import Image from 'next/image';

import { useWindowSize } from 'react-use';

import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import '@splidejs/splide/dist/css/splide.min.css';

import { LandingQuery } from 'generated/operations';
import { useIsVisible } from 'hooks/useIsVisible';

import ProfileImage from 'app/_assets/profile-img.png';

import { ArrowRight } from 'components/icons';

interface IMembers {
  members: LandingQuery['landing']['members'];
}

interface IMember {
  image: string;
  id: string;
  name: string;
  position?: string | null;
  about: string;
  index: number;
}

const SLIDER_BREAKPOINTS = {
  LAPTOP: {
    BREAKPOINT: 1024,
    PER_PAGE: 3
  },
  TABLET: {
    BREAKPOINT: 768,
    PER_PAGE: 2
  },
  MOBILE: {
    BREAKPOINT: 640,
    PER_PAGE: 1
  }
}

type SliderAdditionalConfig = {
  type?: 'loop';
  showArrows: boolean;
  key: number;
}

const Member: FC<IMember> = (props) => {
  const {
    image,
    name,
    position,
    id,
    about,
    index
  } = props;

  return (
    <div
      className="p-9 bg-white h-full flex flex-col items-center rounded-2xl font-poppins text-center gap-6"
    >
      <div className="rounded-full overflow-hidden w-[90px] h-[90px]">
        {image ? (
          <Image src={image as string} alt={`${id}-${name}`} height={90} width={90} />
        ) : (
          <Image src={ProfileImage} alt={`${id}-${name}`} height={90} width={90} />
        )}
      </div>
      <div className="flex flex-col items-center gap-2.5">
        <p className="text-[18px]/[16px] font-medium text-dark-blue">{name}</p>
        {!!position && (
          <p className="text-[#8895aa] font-light text-[14px]/[16px]">{position}</p>
        )}
        <div className="border w-12 border-purple-blue opacity-40" />
        <p className="text-[#8895aa] font-light text-[14px]/[160%]">{about}</p>
      </div>
    </div>
  );
};

const SLIDES_TO_SHOW = 4;

export const Members = forwardRef((props: IMembers, ref: ForwardedRef<HTMLDivElement>) => {
    const { members } = props;

    const { width } = useWindowSize();

    const [splide, setSplide] = useState<Splide | null | undefined>();

    const titleRef = useRef(null);
    const isVisibleTitle = useIsVisible(titleRef);

    const membersArray = useMemo(() => {
      return [...members];
    }, [members]);

    const sliderAdditionalConfig: SliderAdditionalConfig = useMemo(() => {
      if (membersArray.length > SLIDES_TO_SHOW) {
        return {
          showArrows: true,
          key: 1,
          type: undefined,
        };
      } else if (width <= SLIDER_BREAKPOINTS.LAPTOP.BREAKPOINT) {
        if (membersArray.length > SLIDER_BREAKPOINTS.LAPTOP.PER_PAGE) {
          return {
            showArrows: true,
            type: 'loop',
            key: 2,
          };
        } else {
          return {
            showArrows: false,
            key: 3,
            type: undefined,
          }
        }
      } else if (width <= SLIDER_BREAKPOINTS.TABLET.BREAKPOINT) {
        if (membersArray.length > SLIDER_BREAKPOINTS.TABLET.PER_PAGE) {
          return {
            showArrows: true,
            type: 'loop',
            key: 4,
          };
        } else {
          return {
            showArrows: false,
            key: 5,
            type: undefined,
          }
        }
      } else if (width <= SLIDER_BREAKPOINTS.MOBILE.BREAKPOINT) {
        if (membersArray.length > SLIDER_BREAKPOINTS.MOBILE.PER_PAGE) {
          return {
            showArrows: true,
            type: 'loop',
            key: 6,
          };
        } else {
          return {
            showArrows: false,
            key: 7,
            type: undefined,
          }
        }
      }

      return {
        showArrows: false,
        key: 8,
        type: undefined,
      };
    }, [membersArray.length, width]);

    return (
      <div className="flex items-center justify-center p-10 gap-3.5 bg-[#f0f8ff] md:px-5 md:py-10" ref={ref}>
        <div className="container">
          <div className="py-9 px-5 flex flex-col gap-9 justify-between md:p-0">
            <h2
              ref={titleRef}
              className={`font-poppins text-4xl text-dark-blue font-semibold duration-1000 transition-all ${isVisibleTitle ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full'}`}
            >
              Our Members
            </h2>

            <div className={`relative ${sliderAdditionalConfig.showArrows ? 'p-5 md:p-16' : ''}`}>
              <Splide
                key={sliderAdditionalConfig.key}
                hasTrack={false}
                options={{
                  type: sliderAdditionalConfig.type,
                  gap: 30,
                  perPage: SLIDES_TO_SHOW,
                  arrows: false,
                  pagination: false,
                  rewind: false,
                  breakpoints: {
                    [SLIDER_BREAKPOINTS.LAPTOP.BREAKPOINT]: {
                      perPage: SLIDER_BREAKPOINTS.LAPTOP.PER_PAGE,
                    },
                    [SLIDER_BREAKPOINTS.TABLET.BREAKPOINT]: {
                      perPage: SLIDER_BREAKPOINTS.TABLET.PER_PAGE,
                    },
                    [SLIDER_BREAKPOINTS.MOBILE.BREAKPOINT]: {
                      perPage: SLIDER_BREAKPOINTS.MOBILE.PER_PAGE,
                    }
                  }
                }}
                onMounted={(splide) => setSplide(splide as any)}
              >
                <SplideTrack>
                  {membersArray.map((item, index) => (
                    <SplideSlide key={index}>

                      <Member
                        name={item.name}
                        position={item.position ?? ''}
                        about={item.about ?? ''}
                        image={item.image ?? ''}
                        id={item.id}
                        index={index}
                      />
                    </SplideSlide>
                  ))}
                </SplideTrack>
              </Splide>
              {sliderAdditionalConfig.showArrows && (
                <>
                  <button
                    type="button"
                    aria-label="Previous slide"
                    className={`h-11 w-11 flex justify-center items-center bg-purple-blue/20 md:bg-purple-blue hover:bg-purple-blue rounded-full text-white absolute -left-10 top-1/2 -translate-y-1/2 -translate-x-1/2 xl:-translate-x-0 md:left-0 duration-500 transition-all `}
                    onClick={() => {
                      splide?.go('-1');
                    }}
                  >
                    <ArrowRight width={24} height={24} classNames="rotate-180" />
                  </button>
                  <button
                    type="button"
                    aria-label="Next slide"
                    className={`h-11 w-11 flex justify-center items-center bg-purple-blue/20 md:bg-purple-blue hover:bg-purple-blue rounded-full text-white absolute -right-10 top-1/2 -translate-y-1/2 translate-x-1/2 xl:translate-x-0 md:right-0 duration-500 transition-all`}
                    onClick={() => {
                      splide?.go('+1');
                    }}
                  >
                    <ArrowRight width={24} height={24} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

Members.displayName = 'Members';
